import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from "react-icons/io";

import ConfirmDialog from "src/Common/confirmdialog";
import { isClericalOver, isAccountant } from "src/Common/function";
import AccountAddModal from "./CustomerAddModal";
import LocationAddModal from "./LocationAddModal";
import AccountService from "src/Services/API/account.service";
import BranchService from "src/Services/API/branch.service";
import CustomerService from "src/Services/API/customer.service";
import DropDown from "src/Components/EditDeleteDropdown/dropdown";
import { formatDate, getImageUrl } from "src/Common/common";

function Customer() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isAddModalShown, setIsAddModalShown] = useState(false);
  const [isLocationModalShown, setIsLocationModalShown] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [expandedAccountIds, setExpandedAccountIds] = useState([]);
  const [isArchiveConfirmDialogShown, setIsArchiveConfirmDialogShown] = useState(false);
  const [customerForArchive, setCustomerForArchive] = useState(null);
  const [sortByCustomer, setSortByCustomer] = useState(0);

  const sortAccounts = (direction) => {
    setAccounts((prev) => {
      const sorted = [...prev].sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        if (nameA < nameB) return direction === 0 ? -1 : 1;
        if (nameA > nameB) return direction === 0 ? 1 : -1;
        return 0;
      });
      return sorted;
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    BranchService.getAll().then((response) => {
      setBranches(response.data || []);
    });
  }, []);

  const loadData = async () => {
    await Promise.all([getAccounts(), getCustomers()]);
  };

  const getAccounts = async () => {
    try {
      const response = await AccountService.getAll();
      if (response.status === 200) {
        const sorted = [...(response.data || [])].sort((a, b) =>
          (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
        );
        setAccounts(sorted);
        return;
      }

      toast("Something went wrong!");
    } catch (error) {
      console.log("Account load error: ", error);
      toast(error?.response?.data?.message || "Failed to load accounts");
    }
  };

  const getCustomers = async () => {
    try {
      const response = await CustomerService.getAll("");
      if (response.status === 200) {
        setCustomers(response.data || []);
        return;
      }

      toast("Something went wrong!");
    } catch (error) {
      console.log("Customer load error: ", error);
      toast(error?.response?.data?.message || "Failed to load customer locations");
    }
  };

  const addClicked = async (data) => {
    try {
      const response = await AccountService.saveAccount(data);
      if (response.status === 201) {
        if (data.customerIds?.length) {
          await AccountService.assignCustomersToAccount({
            accountId: response.data.id,
            customerIds: data.customerIds,
          });
        }

        toast("Account saved successfully");
        await loadData();
        setIsAddModalShown(false);
        return;
      }

      toast("Something went wrong");
    } catch (error) {
      console.log("Account save error: ", error);
      toast(error?.response?.data?.message || "Failed to save account");
    }
  };

  const updateClicked = async (data) => {
    try {
      const response = await AccountService.updateAccount({
        id: data.id,
        name: data.name,
      });
      if (response.status === 201) {
        await AccountService.assignCustomersToAccount({
          accountId: data.id,
          customerIds: data.customerIds || [],
        });

        toast("Account updated successfully");
        setExpandedAccountIds((previous) =>
          previous.includes(data.id) ? previous : [...previous, data.id]
        );
        await loadData();
        setEditingAccount(null);
        setIsAddModalShown(false);
        return;
      }

      toast("Something went wrong");
    } catch (error) {
      console.log("Account update error: ", error);
      toast(error?.response?.data?.message || "Failed to update account");
    }
  };

  const addLocationClicked = async (data) => {
    try {
      const { accountId, ...locationData } = data;
      const response = await CustomerService.saveCustomer(locationData);
      if (response.status === 201) {
        if (accountId) {
          const selectedAccount = accounts.find((account) => account.id === accountId);
          const existingCustomerIds = (selectedAccount?.customers || []).map(
            (customer) => customer.id
          );

          await AccountService.assignCustomersToAccount({
            accountId,
            customerIds: [...new Set([...existingCustomerIds, response.data.id])],
          });
        }

        toast("Location saved successfully");
        await loadData();
        setIsLocationModalShown(false);
        return;
      }

      toast("Something went wrong");
    } catch (error) {
      console.log("Location save error: ", error);
      toast(error?.response?.data?.message || "Failed to save location");
    }
  };

  const updateLocationClicked = async (customer) => {
    try {
      const response = await CustomerService.updateCustomer(customer);
      if (response.status === 201) {
        toast("Location updated successfully");
        await loadData();
        setEditingLocation(null);
        setIsLocationModalShown(false);
        return;
      }

      toast("Something went wrong");
    } catch (error) {
      console.log("Location update error: ", error);
      toast(error?.response?.data?.message || "Failed to update location");
    }
  };

  const archiveLocationClicked = (customer) => {
    setCustomerForArchive(customer);
    setIsArchiveConfirmDialogShown(true);
  };

  const confirmArchiveLocationClicked = async () => {
    if (!customerForArchive) {
      return;
    }

    try {
      const response = await CustomerService.archiveCustomer(customerForArchive.id);
      if (response.status === 201) {
        toast("Location archived successfully");
        await loadData();
        setCustomerForArchive(null);
        setIsArchiveConfirmDialogShown(false);
        return;
      }

      toast("Something went wrong");
    } catch (error) {
      console.log("Location archive error: ", error);
      toast(error?.response?.data?.message || "Failed to archive location");
    }
  };

  const editLocationClicked = (customer) => {
    setEditingLocation(customer);
    setIsLocationModalShown(true);
  };

  const toggleAccountExpanded = (accountId) => {
    setExpandedAccountIds((previous) => {
      if (previous.includes(accountId)) {
        return previous.filter((id) => id !== accountId);
      }

      return [...previous, accountId];
    });
  };

  const expandAllAccounts = () => {
    setExpandedAccountIds(accounts.map((account) => account.id));
  };

  const collapseAllAccounts = () => {
    setExpandedAccountIds([]);
  };

  const getBranchSummary = (accountCustomers) => {
    const branchNames = [...new Set(
      accountCustomers
        .map((customer) => customer.branch?.name)
        .filter(Boolean)
    )];

    return branchNames.join("/") || "";
  };

  return (
    <div className="w-full h-full px-3 bg-backgroundColor">
      {isArchiveConfirmDialogShown && (
        <ConfirmDialog
          title="ARCHIVE LOCATION"
          description="Are you sure you want to archive this?"
          shown={isArchiveConfirmDialogShown}
          onConfirmClicked={confirmArchiveLocationClicked}
          onCancelClicked={() => {
            setCustomerForArchive(null);
            setIsArchiveConfirmDialogShown(false);
          }}
        />
      )}
      {isAddModalShown && (
        <AccountAddModal
          account={editingAccount}
          customers={customers}
          onAddClicked={addClicked}
          onUpdateClicked={updateClicked}
          onCancelClicked={() => {
            setEditingAccount(null);
            setIsAddModalShown(false);
          }}
        />
      )}
      {isLocationModalShown && (
        <LocationAddModal
          branches={branches}
          accounts={accounts}
          customers={customers}
          onAddClicked={addLocationClicked}
          onUpateClicked={updateLocationClicked}
          onCancelClicked={() => {
            setEditingLocation(null);
            setIsLocationModalShown(false);
          }}
          customer={editingLocation}
        />
      )}

      <div className="flex items-center justify-between gap-3 h-[62px] border-b-[1px] border-borderColor">
        <div className="flex items-center gap-3">
          <div
            onClick={expandAllAccounts}
            className="h-[30px] w-[30px] flex items-center justify-center cursor-pointer border-[1px] border-borderColor rounded-[5px] "
          >
            <span className="text-[14px] font-[600] font-OpenSansRegular text-subTitleColor px-2 py-1">
              +
            </span>
          </div>
          <div
            onClick={collapseAllAccounts}
            className="h-[30px] w-[30px] flex items-center justify-center cursor-pointer border-[1px] border-borderColor rounded-[5px]"
          >
            <span className="text-[14px] font-[600] font-OpenSansRegular text-subTitleColor  px-2 py-1">
              -
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            onClick={() => {
              setEditingAccount(null);
              setIsAddModalShown(true);
            }}
            className="h-[30px] px-3 flex items-center justify-center cursor-pointer"
          >
            <span className="text-[12px] font-[600] font-OpenSansRegular text-subTitleColor">
              + Customer
            </span>
          </div>
          <div
            onClick={() => {
              setEditingLocation(null);
              setIsLocationModalShown(true);
            }}
            className="h-[30px] px-3 flex items-center justify-center cursor-pointer"
          >
            <span className="text-[12px] font-[600] font-OpenSansRegular text-subTitleColor">
              + Location
            </span>
          </div>
        </div>

      </div>

      <table className="w-full table-fixed">
        <thead>
          <tr className="border-b-[1px] border-borderColor">
            <th className="w-[70px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                No.
              </div>
            </th>
            <th
              onClick={() => {
                if (sortByCustomer === 1) {
                  setSortByCustomer(0);
                  sortAccounts(0);
                } else if (sortByCustomer === 0) {
                  setSortByCustomer(1);
                  sortAccounts(1);
                }
              }}
              className="min-w-[230px] border-r-[1px] border-borderColor cursor-pointer flex items-center justify-between pl-1 pr-3"
            >
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Customer/Location(s)
              </div>
                {sortByCustomer === 0 ? <IoIosArrowRoundUp /> : <IoIosArrowRoundDown />}
            </th>
            <th className="w-[170px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Contact Name
              </div>
            </th>
            <th className="w-[180px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Contact Email
              </div>
            </th>
            <th className="w-[140px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Contact Phone #
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Address
              </div>
            </th>
            <th className="w-[250px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Branch(s)
              </div>
            </th>
            <th className="w-[120px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Added By
              </div>
            </th>
            <th className="w-[120px] border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                Date Added
              </div>
            </th>
            <th className="w-[40px]"></th>
          </tr>
        </thead>
        <tbody>
          {accounts.length === 0 ? (
            <tr className="border-b-[1px] border-borderColor h-[40px]">
              <td colSpan={10}>
                <div className="font-OpenSansRegular px-[15px] py-[10px] text-[12px] font-[400] text-subTitleColor">
                  No accounts added yet.
                </div>
              </td>
            </tr>
          ) : (
            accounts.map((account, index) => {
              const isExpanded = expandedAccountIds.includes(account.id);
              const accountCustomers = account.customers || [];
              const branchSummary = getBranchSummary(accountCustomers);

              return (
                <Fragment key={account.id || index}>
                  <tr
                    onClick={() => toggleAccountExpanded(account.id)}
                    className="border-b-[1px] border-borderColor h-[40px] cursor-pointer bg-white"
                  >
                    <td className="border-r-[1px] border-borderColor">
                      <div className="font-OpenSansRegular px-[15px] py-[5px] text-[12px] font-[400]">
                        {index + 1}
                      </div>
                    </td>
                    <td className="border-r-[1px] border-borderColor">
                      <div className="font-OpenSansRegular px-[15px] py-[5px] text-[12px] font-[600] flex items-center justify-between gap-4 word-break-break-word text-titleColor">
                        <span>{account.name}</span>
                        <div className="flex items-center gap-2">
                          {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                          <span className="text-titleColor whitespace-nowrap">
                            {accountCustomers.length}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="border-r-[1px] border-borderColor"></td>
                    <td className="border-r-[1px] border-borderColor"></td>
                    <td className="border-r-[1px] border-borderColor"></td>
                    <td className="border-r-[1px] border-borderColor"></td>
                    <td className="border-r-[1px] border-borderColor">
                      <div className="font-OpenSansRegular px-[15px] py-[5px] text-[12px] font-[400] text-titleColor">
                        {branchSummary}
                      </div>
                    </td>
                    <td className="border-r-[1px] border-borderColor"></td>
                    <td className="border-r-[1px] border-borderColor"></td>
                    <td>
                      <div className="px-[10px] py-[5px] flex justify-center">
                        <img
                          src={getImageUrl("/images/settings/edit.png")}
                          alt="Edit account"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingAccount(account);
                            setIsAddModalShown(true);
                          }}
                          className="w-[16px] h-[16px] cursor-pointer"
                        />
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    accountCustomers.length === 0 ? (
                      <tr className="border-b-[1px] border-borderColor">
                        <td className="border-r-[1px] border-borderColor"></td>
                        <td colSpan={9}>
                          <div className="font-OpenSansRegular px-[15px] py-[10px] text-[12px] font-[400] text-subTitleColor">
                            No customer locations have been added to this customer yet.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      accountCustomers.map((customer) => (
                        <tr
                          key={customer.id}
                          onClick={() => navigate(`/customer/${customer.id}`)}
                          className="border-b-[1px] border-borderColor bg-[#FCFCFC] cursor-pointer"
                        >
                          <td className="border-r-[1px] border-borderColor"></td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor pl-8">
                              {customer.companyName || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor">
                              {customer.contact || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor break-words">
                              {customer.email || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor">
                              {customer.phone || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor break-words">
                              {customer.address || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor">
                              {customer.branch?.name || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor">
                              {customer.createdBy?.name || ""}
                            </div>
                          </td>
                          <td className="border-r-[1px] border-borderColor">
                            <div className="font-OpenSansRegular px-[15px] py-[8px] text-[11px] font-[400] text-titleColor">
                              {customer.createdAt
                                ? formatDate(customer.createdAt, "MM/DD/YYYY")
                                : ""}
                            </div>
                          </td>
                          <td
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {(isClericalOver() || isAccountant()) && (
                              <DropDown
                                index={customer}
                                onDeleteClicked={archiveLocationClicked}
                                onEditClicked={editLocationClicked}
                              />
                            )}
                          </td>
                        </tr>
                      ))
                    )
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Customer;
